import React, { useMemo, useState } from "react"
import { Button, IconButton, Tooltip } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart"
import Input from "../../components/Input"
import AutocompleteDeluxe from "../../components/AutocompleteDelux"
import Vista from "../../components/Vista"
import { useAlert } from "../../hooks/useAlert"
import pdfOrdenCompra from "../../pdfs/pdfOrdenCompra"
import "./OrdenCompraServicio.css"

const obtenerProducto = (item) => item?.productos || item?.producto || {}
const obtenerPrecio = (item) => Number(item?.precioPublico || item?.precio || item?.productos?.precioPublico || item?.producto?.precioPublico || 0)
const formatoMoneda = (cantidad) => Number(cantidad || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" })

const OrdenCompraServicio = ({ guardarOrdenCompra, ordenesCompra, productosOrdenCompra, setProductosOrdenCompra, loadingButton = false }) => {
  const { showAlert } = useAlert()
  const [productoCompra, setProductoCompra] = useState(null)
  const [ordenSeleccionada, setOrdenSeleccionada] = useState("")
  const [isOrdenCompraLocked, setIsOrdenCompraLocked] = useState(false)

  const opcionesOrdenesCompra = useMemo(() => [
    { value: "", label: "Selecciona una orden generada" },
    ...(Array.isArray(ordenesCompra) ? ordenesCompra.map((orden) => ({
      value: String(orden.idOrdenCompra),
      label: `OC-${orden.idOrdenCompra} · Orden de servicio ${orden.idOrdenServicio}`
    })) : [])
  ], [ordenesCompra])

  const totalOrden = useMemo(() => productosOrdenCompra.reduce(
    (total, item) => total + obtenerPrecio(item) * Number(item.cantidad || 0), 0
  ), [productosOrdenCompra])

  const handleSeleccionOrdenCompra = (idOrdenCompra) => {
    const id = String(idOrdenCompra || "")
    setOrdenSeleccionada(id)
    if (!id) return
    const orden = ordenesCompra.find((item) => String(item.idOrdenCompra) === id)
    if (!orden) {
      showAlert({ message: "La orden de compra seleccionada no es válida", severity: "error" })
      return
    }
    setProductosOrdenCompra(orden.ordenesCompraRefacciones || [])
    setIsOrdenCompraLocked(true)
  }

  const prepararNuevaOrden = () => {
    setOrdenSeleccionada("")
    setProductosOrdenCompra([])
    setProductoCompra(null)
    setIsOrdenCompraLocked(false)
  }

  const addProductCompra = () => {
    if (!productoCompra) {
      showAlert({ message: "Selecciona una refacción o producto", severity: "warning" })
      return
    }
    const repetido = productosOrdenCompra.some((item) => Number(item.idProducto) === Number(productoCompra.idProducto))
    if (repetido) {
      showAlert({ message: "Este producto ya está incluido en la orden", severity: "warning" })
      return
    }
    const precio = Number(productoCompra.precioPublico || 0)
    setProductosOrdenCompra((actuales) => [...actuales, {
      producto: productoCompra,
      idProducto: productoCompra.idProducto,
      categoria: productoCompra.categoria || "OTROS",
      precioPublico: precio,
      precio,
      subtotal: precio,
      idProveedor: null,
      proveedor: null,
      cantidad: 1
    }])
    setProductoCompra(null)
  }

  const changeCantidadCompra = (cantidad, index) => {
    const qty = Math.max(1, Number(cantidad) || 1)
    setProductosOrdenCompra((actuales) => actuales.map((item, posicion) => posicion === index
      ? { ...item, cantidad: qty, subtotal: obtenerPrecio(item) * qty }
      : item
    ))
  }

  const deleteProductOrdenCompra = (index) => {
    setProductosOrdenCompra((actuales) => actuales.filter((_, posicion) => posicion !== index))
  }

  const handleProveedorChange = (proveedor, index) => {
    setProductosOrdenCompra((actuales) => actuales.map((item, posicion) => posicion === index
      ? { ...item, proveedor, proveedores: proveedor, idProveedor: proveedor?.idProveedor || null }
      : item
    ))
  }

  const handleGeneratePDF = async () => {
    const orden = ordenesCompra.find((item) => item.idOrdenCompra === Number(ordenSeleccionada))
    if (!orden) {
      showAlert({ message: "Selecciona una orden de compra para generar el PDF", severity: "warning" })
      return
    }
    try {
      const pdfBlob = await pdfOrdenCompra(orden)
      const url = window.URL.createObjectURL(pdfBlob)
      const enlace = document.createElement("a")
      enlace.href = url
      enlace.download = `Orden_Compra_C${orden.idOrdenCompra}_OS${orden.idOrdenServicio}.pdf`
      document.body.appendChild(enlace)
      enlace.click()
      enlace.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      showAlert({ message: "No fue posible generar el PDF", severity: "error" })
    }
  }

  return (
    <Vista>
      <section className="purchase-order" aria-labelledby="purchase-order-title">
        <header className="purchase-order__hero">
          <div>
            <span>Compras vinculadas</span>
            <h2 id="purchase-order-title">Órdenes de compra</h2>
            <p>Consulta compras anteriores o prepara una nueva para esta orden de servicio.</p>
          </div>
          <span className="purchase-order__counter">{ordenesCompra.length} {ordenesCompra.length === 1 ? "orden" : "órdenes"}</span>
        </header>

        <article className="purchase-order__panel purchase-order__history">
          <div className="purchase-order__heading">
            <div><h3>Órdenes generadas</h3><p>Selecciona una orden para revisar sus productos o descargarla.</p></div>
            <Button variant="outlined" startIcon={<AddShoppingCartIcon />} onClick={prepararNuevaOrden}>Nueva orden</Button>
          </div>
          <div className="purchase-order__history-controls">
            <Input nombre="Orden de compra" typeInput="formSelect" data={opcionesOrdenesCompra} valor={ordenSeleccionada} value={ordenSeleccionada} onChange={(event) => handleSeleccionOrdenCompra(event.target.value)} />
            <Button className="purchase-order__pdf" variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!ordenSeleccionada} onClick={handleGeneratePDF}>Descargar PDF</Button>
          </div>
        </article>

        {!isOrdenCompraLocked && (
          <article className="purchase-order__panel purchase-order__add">
            <div className="purchase-order__heading"><div><h3>Agregar productos</h3><p>Busca directamente por nombre o número de parte.</p></div></div>
            <div className="purchase-order__add-controls">
              <AutocompleteDeluxe labelToShow="Refacción o producto" labelProp="nombreProducto" idProp="idProducto" concat="numParte" nombre="productos" dato={productoCompra} setDato={setProductoCompra} background="#F2F2F2" />
              <Button variant="contained" startIcon={<AddIcon />} disabled={!productoCompra} onClick={addProductCompra}>Agregar</Button>
            </div>
          </article>
        )}

        <article className="purchase-order__panel purchase-order__detail">
          <div className="purchase-order__heading purchase-order__detail-heading">
            <div><h3>{isOrdenCompraLocked ? "Detalle de la orden" : "Nueva orden de compra"}</h3><p>{isOrdenCompraLocked ? "Esta orden ya fue generada y es solo de consulta." : "Asigna cantidades y proveedor antes de guardar."}</p></div>
            <strong className="purchase-order__total">Total: {formatoMoneda(totalOrden)}</strong>
          </div>

          {productosOrdenCompra.length ? (
            <ul className="purchase-order__items">
              {productosOrdenCompra.map((item, index) => {
                const producto = obtenerProducto(item)
                const precio = obtenerPrecio(item)
                return (
                  <li key={`${item.idProducto}-${index}`}>
                    <div className="purchase-order__item-index">{index + 1}</div>
                    <div className="purchase-order__product"><strong>{producto.nombreProducto || "Producto sin nombre"}</strong><span>{producto.numParte || "Sin número de parte"}</span></div>
                    <div className="purchase-order__price"><span>Precio</span><strong>{formatoMoneda(precio)}</strong></div>
                    <div className="purchase-order__quantity">
                      <span>Cantidad</span>
                      <div>
                        <IconButton disabled={isOrdenCompraLocked || Number(item.cantidad) <= 1} onClick={() => changeCantidadCompra(Number(item.cantidad) - 1, index)}><RemoveIcon /></IconButton>
                        <strong>{item.cantidad}</strong>
                        <IconButton disabled={isOrdenCompraLocked} onClick={() => changeCantidadCompra(Number(item.cantidad) + 1, index)}><AddIcon /></IconButton>
                      </div>
                    </div>
                    <div className="purchase-order__subtotal"><span>Subtotal</span><strong>{formatoMoneda(precio * Number(item.cantidad || 0))}</strong></div>
                    <div className="purchase-order__provider">
                      <AutocompleteDeluxe labelToShow="Proveedor" labelProp="nombre" idProp="idProveedor" disabled={isOrdenCompraLocked} nombre="proveedores" dato={item.proveedor || item.proveedores || null} setDato={(proveedor) => handleProveedorChange(proveedor, index)} background="#FFFFFF" />
                    </div>
                    {!isOrdenCompraLocked && (
                      <Tooltip title="Quitar producto"><IconButton className="purchase-order__delete" onClick={() => deleteProductOrdenCompra(index)} aria-label={`Quitar ${producto.nombreProducto}`}><DeleteOutlineIcon /></IconButton></Tooltip>
                    )}
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="purchase-order__empty"><AddShoppingCartIcon /><strong>La nueva orden está vacía</strong><span>Busca y agrega los productos que se necesitan comprar.</span></div>
          )}

          {!isOrdenCompraLocked && (
            <footer className="purchase-order__footer">
              <div><span>Total de la orden</span><strong>{formatoMoneda(totalOrden)}</strong></div>
              <LoadingButton variant="contained" loading={loadingButton} disabled={!productosOrdenCompra.length} onClick={() => guardarOrdenCompra(productosOrdenCompra)}>Guardar orden de compra</LoadingButton>
            </footer>
          )}
        </article>
      </section>
    </Vista>
  )
}

export default OrdenCompraServicio
